import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  Popconfirm,
  Space,
  Tag,
  Image,
  Typography,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import {
  getAllSlidersAction,
  createSliderAction,
  updateSliderAction,
  deleteSliderAction,
  toggleSliderStatusAction,
  getAllCategoriesAction,
  getAllProductsAction,
} from '@app/store/slices/userSlice';
import config from '@app/config';

const { Title, Text } = Typography;
const { Option } = Select;

interface SliderItem {
  _id: string;
  title?: string;
  description?: string;
  photo?: {
    _id: string;
    filePath: string;
    originalName?: string;
  };
  image_url?: string;
  redirect_type: 'none' | 'category' | 'product' | 'wallet' | 'subscription' | 'external_url';
  redirect_id?: string;
  order_priority: number;
  is_active: boolean;
  createdAt?: string;
}

export const ManageSliders: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [sliders, setSliders] = useState<SliderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTitle, setSearchTitle] = useState('');

  // Categories & Products for redirect dropdown
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState<SliderItem | null>(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [selectedRedirectType, setSelectedRedirectType] = useState<string>('none');

  useEffect(() => {
    fetchSliders();
    fetchOptions();
  }, [page, pageSize]);

  const fetchOptions = async () => {
    try {
      const catRes: any = await dispatch(getAllCategoriesAction({ page: 1, pageSize: 100 })).unwrap();
      if (catRes?.data) setCategories(catRes.data);

      const prodRes: any = await dispatch(getAllProductsAction({ page: 1, pageSize: 200 })).unwrap();
      if (prodRes?.data) setProducts(prodRes.data);
    } catch (e) {
      // ignore
    }
  };

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        pageSize,
        filters: {},
      };
      if (searchTitle.trim()) {
        params.filters.title = searchTitle.trim();
      }

      const res: any = await dispatch(getAllSlidersAction({ search: JSON.stringify(params) })).unwrap();
      if (res?.data) {
        setSliders(res.data);
        setTotal(res.total || res.data.length);
      }
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to load sliders' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingSlider(null);
    form.resetFields();
    setSelectedPhotoId(null);
    setFileList([]);
    setPreviewImage('');
    setSelectedRedirectType('none');
    form.setFieldsValue({
      redirect_type: 'none',
      order_priority: 0,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (slider: SliderItem) => {
    setEditingSlider(slider);
    form.resetFields();
    setSelectedPhotoId(slider.photo?._id || null);
    setSelectedRedirectType(slider.redirect_type || 'none');

    let initialImg = slider.image_url || '';
    if (slider.photo?.filePath) {
      initialImg = slider.photo.filePath.startsWith('http')
        ? slider.photo.filePath
        : `${config.API1Url}/${slider.photo.filePath}`;
    }
    setPreviewImage(initialImg);

    if (slider.photo) {
      setFileList([
        {
          uid: '-1',
          name: slider.photo.originalName || 'banner_image',
          status: 'done',
          url: initialImg,
        },
      ]);
    } else {
      setFileList([]);
    }

    form.setFieldsValue({
      title: slider.title || '',
      description: slider.description || '',
      image_url: slider.image_url || '',
      redirect_type: slider.redirect_type || 'none',
      redirect_id: slider.redirect_id || '',
      order_priority: slider.order_priority ?? 0,
      is_active: slider.is_active ?? true,
    });

    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedPhotoId && !values.image_url) {
        notificationController.warning({ message: 'Please upload a banner image or enter an image URL.' });
        return;
      }

      setModalSubmitting(true);
      const payload: any = {
        title: values.title || '',
        description: values.description || '',
        photo: selectedPhotoId || null,
        image_url: values.image_url || '',
        redirect_type: values.redirect_type || 'none',
        redirect_id: values.redirect_id || '',
        order_priority: Number(values.order_priority) || 0,
        is_active: values.is_active !== undefined ? Boolean(values.is_active) : true,
      };

      if (editingSlider) {
        const res: any = await dispatch(updateSliderAction({ id: editingSlider._id, payload })).unwrap();
        if (res?.success) {
          notificationController.success({ message: 'Slider banner updated successfully!' });
        }
      } else {
        const res: any = await dispatch(createSliderAction(payload)).unwrap();
        if (res?.success) {
          notificationController.success({ message: 'Slider banner created successfully!' });
        }
      }

      setIsModalOpen(false);
      fetchSliders();
    } catch (err: any) {
      if (err?.errorFields) return;
      notificationController.error({ message: err?.message || err || 'Failed to save slider banner.' });
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteSliderAction(id)).unwrap();
      notificationController.success({ message: 'Slider banner deleted successfully.' });
      fetchSliders();
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to delete slider' });
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleSliderStatusAction(id)).unwrap();
      notificationController.success({ message: 'Slider status updated.' });
      fetchSliders();
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to update status' });
    }
  };

  const handleUploadChange = (info: any) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1); // Only keep the latest uploaded file

    if (info.file.status === 'done') {
      const uploadedFileId = info.file.response?.file?._id;
      const uploadedFilePath = info.file.response?.file?.filePath;
      if (uploadedFileId) {
        setSelectedPhotoId(uploadedFileId);
        if (uploadedFilePath) {
          setPreviewImage(`${config.API1Url}/${uploadedFilePath}`);
        }
        notificationController.success({ message: 'Image uploaded successfully!' });
      }
    } else if (info.file.status === 'error') {
      notificationController.error({ message: 'Image upload failed.' });
    }

    setFileList(newFileList);
  };

  const columns = [
    {
      title: 'Image Preview',
      key: 'preview',
      width: 140,
      render: (_: any, record: SliderItem) => {
        let src = record.image_url || '';
        if (record.photo?.filePath) {
          src = record.photo.filePath.startsWith('http')
            ? record.photo.filePath
            : `${config.API1Url}/${record.photo.filePath}`;
        }
        return src ? (
          <Image
            src={src}
            alt={record.title || 'banner'}
            style={{ width: 110, height: 55, objectFit: 'cover', borderRadius: 6 }}
            fallback="https://via.placeholder.com/220x110?text=No+Image"
          />
        ) : (
          <Tag icon={<PictureOutlined />}>No Image</Tag>
        );
      },
    },
    {
      title: 'Title & Description',
      key: 'info',
      render: (_: any, record: SliderItem) => (
        <div>
          <Text strong style={{ fontSize: 14, display: 'block' }}>
            {record.title || <span style={{ color: '#999' }}>(No Title)</span>}
          </Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Action / Redirect',
      key: 'redirect',
      width: 180,
      render: (_: any, record: SliderItem) => {
        const type = record.redirect_type;
        if (type === 'none') return <Tag color="default">None</Tag>;
        if (type === 'wallet') return <Tag color="blue">Wallet Recharge</Tag>;
        if (type === 'subscription') return <Tag color="purple">Subscription</Tag>;
        if (type === 'category') {
          const cat = categories.find((c) => c._id === record.redirect_id);
          return <Tag color="green">Category: {cat?.name || record.redirect_id || 'All'}</Tag>;
        }
        if (type === 'product') {
          const prod = products.find((p) => p._id === record.redirect_id);
          return <Tag color="cyan">Product: {prod?.name || record.redirect_id || 'Selected'}</Tag>;
        }
        if (type === 'external_url') {
          return (
            <Tooltip title={record.redirect_id}>
              <Tag color="orange" icon={<LinkOutlined />}>
                Web Link
              </Tag>
            </Tooltip>
          );
        }
        return <Tag>{type}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'order_priority',
      key: 'order_priority',
      width: 90,
      align: 'center' as const,
      render: (priority: number) => <Tag color="geekblue">#{priority}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      align: 'center' as const,
      render: (isActive: boolean, record: SliderItem) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record._id)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: SliderItem) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(record)}
            title="Edit Banner"
          />
          <Popconfirm
            title="Are you sure you want to delete this slider banner?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Delete Banner" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Home Screen Sliders & Banners
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Configure dynamic promotional carousel banners displayed on the mobile app home screen.
              </Text>
            </Col>
            <Col>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchSliders}>
                  Refresh
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAddModal}>
                  Add New Banner
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        {/* Search & Filter */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="Search by banner title..."
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              onSearch={() => {
                setPage(1);
                fetchSliders();
              }}
              allowClear
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={sliders}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editingSlider ? 'Edit Slider Banner' : 'Add New Slider Banner'}
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleModalSubmit}
        confirmLoading={modalSubmitting}
        okText={editingSlider ? 'Save Changes' : 'Create Banner'}
        width={650}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Upload Banner Image (Recommended aspect ratio 16:7)">
                <Upload
                  action={`${config.API1Url}/api/files/upload`}
                  listType="picture"
                  fileList={fileList}
                  onChange={handleUploadChange}
                  beforeUpload={(file) => {
                    const isImg = file.type.startsWith('image/');
                    if (!isImg) {
                      notificationController.error({ message: 'Only image files are allowed!' });
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Select Banner Image</Button>
                </Upload>
                {previewImage && (
                  <div style={{ marginTop: 10 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Current Image Preview:
                    </Text>
                    <Image
                      src={previewImage}
                      alt="preview"
                      style={{ maxHeight: 120, borderRadius: 6, objectFit: 'contain' }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="image_url"
                label="Or Direct Image URL (Optional fallback or CDN/S3 URL)"
              >
                <Input
                  placeholder="https://example.com/banner.jpg"
                  onChange={(e) => {
                    if (e.target.value && !selectedPhotoId) {
                      setPreviewImage(e.target.value);
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="title" label="Banner Title (Optional)">
                <Input placeholder="e.g. Order till Midnight" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="order_priority" label="Sort Priority (Lower numbers appear first)">
                <InputNumber style={{ width: '100%' }} min={0} max={9999} placeholder="0" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="description" label="Subtitle / Description (Optional)">
                <Input.TextArea rows={2} placeholder="e.g. Get your fresh dairy delivered by morning" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="redirect_type" label="On-Click Action">
                <Select
                  onChange={(val) => {
                    setSelectedRedirectType(val);
                    form.setFieldsValue({ redirect_id: '' });
                  }}
                >
                  <Option value="none">None (Informational only)</Option>
                  <Option value="category">Open Category</Option>
                  <Option value="product">Open Product Details</Option>
                  <Option value="wallet">Open Wallet / Add Money</Option>
                  <Option value="subscription">Open Subscription Page</Option>
                  <Option value="external_url">Open External Web Link</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              {selectedRedirectType === 'category' && (
                <Form.Item name="redirect_id" label="Select Category" rules={[{ required: true, message: 'Please select category' }]}>
                  <Select placeholder="Choose category">
                    {categories.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {selectedRedirectType === 'product' && (
                <Form.Item name="redirect_id" label="Select Product" rules={[{ required: true, message: 'Please select product' }]}>
                  <Select placeholder="Choose product" showSearch filterOption={(input, option: any) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {products.map((p) => (
                      <Option key={p._id} value={p._id}>
                        {p.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {selectedRedirectType === 'external_url' && (
                <Form.Item name="redirect_id" label="Website URL" rules={[{ required: true, message: 'Please enter URL' }]}>
                  <Input placeholder="https://..." />
                </Form.Item>
              )}

              {['none', 'wallet', 'subscription'].includes(selectedRedirectType) && (
                <Form.Item label="Target">
                  <Input disabled value="Predefined in App" />
                </Form.Item>
              )}
            </Col>

            <Col span={12}>
              <Form.Item name="is_active" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageSliders;
